import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Grid,
  Card,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import CalculateIcon from "@mui/icons-material/Calculate";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import PieChartIcon from "@mui/icons-material/PieChart";
import LockClockIcon from "@mui/icons-material/LockClock";
import TimelineIcon from "@mui/icons-material/Timeline";
import CasinoIcon from "@mui/icons-material/Casino";
import TuneIcon from "@mui/icons-material/Tune";
import { colors } from "../../../../../theme";
import ThreeSlotSymbolIcon from "./ThreeSlotSymbolIcon";
import {
  FIAT_SET_SYMBOLS,
  CRYPTO_SET_SYMBOLS,
  DEFAULT_LINE_MULTIPLIERS,
  DEFAULT_TOTAL_LINES,
  getThreeSlotPaylines,
} from "./threeSlotConfig";

export const ThreeSlotRulesSection = ({ lotteryData }) => {
  const [selectedThemeTab, setSelectedThemeTab] = useState("fiat");
  const [expandedPanel, setExpandedPanel] = useState("panel-gameplay");

  const currency = lotteryData?.currency || "BET";
  const ticketPrice = Number(lotteryData?.ticketPrice || 10);
  const activePaylines =
    Array.isArray(lotteryData?.paylines) && lotteryData.paylines.length > 0
      ? lotteryData.paylines
      : getThreeSlotPaylines(
          lotteryData?.lineMultipliers || DEFAULT_LINE_MULTIPLIERS,
          lotteryData?.totalLines || DEFAULT_TOTAL_LINES
        );

  const symbols = selectedThemeTab === "crypto" ? CRYPTO_SET_SYMBOLS : FIAT_SET_SYMBOLS;

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  return (
    <Box sx={{ mt: 4, mb: 4, width: "100%" }}>
      {/* Top Section Header */}
      <Card
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: "16px",
          bgcolor: "#ffffff",
          border: `1.5px solid ${colors.slate[200] || "#e2e8f0"}`,
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.04)",
          mb: 2.5,
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                boxShadow: "0 4px 12px rgba(139, 92, 246, 0.25)",
              }}
            >
              <MenuBookIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900, color: colors.slate[900], lineHeight: 1.2 }}>
                How the Game Works & Official Rules
              </Typography>
              <Typography variant="caption" sx={{ color: colors.slate[600], display: "block", mt: 0.25 }}>
                Complete mathematical guide to 3-SlotBET mechanics, 5 paylines, flexible betting, split economics & payouts
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label="3 Reels × 3 Rows"
              size="small"
              sx={{ bgcolor: "#faf5ff", color: "#8b5cf6", fontWeight: 800, border: "1px solid #d8b4fe" }}
            />
            <Chip
              label="5 Dedicated Paylines"
              size="small"
              sx={{ bgcolor: "#fefce8", color: "#b45309", fontWeight: 800, border: "1px solid #fde047" }}
            />
          </Stack>
        </Stack>
      </Card>

      {/* Accordion Group for the 10 Rule Sections */}
      <Stack spacing={2}>
        {/* SECTION 1 & 1b: GAME SETUP & FLEXIBLE PAYLINE SELECTION */}
        <Accordion
          expanded={expandedPanel === "panel-gameplay"}
          onChange={handleAccordionChange("panel-gameplay")}
          sx={{
            borderRadius: "14px !important",
            border: `1.5px solid ${expandedPanel === "panel-gameplay" ? "#8b5cf6" : colors.slate[200]}`,
            boxShadow: expandedPanel === "panel-gameplay" ? "0 4px 16px rgba(139, 92, 246, 0.08)" : "none",
            "&:before": { display: "none" },
            overflow: "hidden",
            bgcolor: "#ffffff",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#8b5cf6" }} />}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <SportsEsportsIcon sx={{ color: "#8b5cf6" }} />
              <Typography sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "0.95rem" }}>
                1. The Game Setup & Flexible Payline Selection
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2.5, px: { xs: 2, sm: 3 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "#f8fafc", border: `1px solid ${colors.slate[200]}`, height: "100%" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#8b5cf6", mb: 0.5 }}>
                    Screen Layout (3×3 Grid = 9 Positions)
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.slate[600], lineHeight: 1.6 }}>
                    The screen consists of <strong>3 spinning reels (columns) and 3 rows</strong>, forming <strong>9 distinct symbol positions</strong>. Each reel spins independently and reveals 3 symbols upon stopping.
                  </Typography>
                  <Box sx={{ mt: 1.5, p: 1, borderRadius: "8px", bgcolor: "#faf5ff", border: "1px dashed #d8b4fe" }}>
                    <Typography variant="caption" sx={{ color: "#6b21a8", fontWeight: 700 }}>
                      🎰 Fair RNG evaluates the stopping positions for all 9 icons (values 0 to 4) directly verified on-chain.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "#f8fafc", border: `1px solid ${colors.slate[200]}`, height: "100%" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#8b5cf6", mb: 0.5 }}>
                    Creator Setup & Player Payline Freedom
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.slate[600], lineHeight: 1.6 }}>
                    • <strong>Creator Setup:</strong> The lottery creator configures the base ticket price (e.g. {ticketPrice} {currency}) and active paylines ({activePaylines.length} lines) when initiating the lottery.
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.slate[600], lineHeight: 1.6, mt: 0.5 }}>
                    • <strong>Player Flexibility:</strong> Players are not locked into fixed lines. You can choose to activate <strong>any combination from 1 up to {activePaylines.length} lines</strong> to customize your strategy!
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* SECTION 2: THE 5 SYMBOLS & WHAT THEY PAY */}
        <Accordion
          expanded={expandedPanel === "panel-symbols"}
          onChange={handleAccordionChange("panel-symbols")}
          sx={{
            borderRadius: "14px !important",
            border: `1.5px solid ${expandedPanel === "panel-symbols" ? "#8b5cf6" : colors.slate[200]}`,
            boxShadow: expandedPanel === "panel-symbols" ? "0 4px 16px rgba(139, 92, 246, 0.08)" : "none",
            "&:before": { display: "none" },
            overflow: "hidden",
            bgcolor: "#ffffff",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#8b5cf6" }} />}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CasinoIcon sx={{ color: "#8b5cf6" }} />
              <Typography sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "0.95rem" }}>
                2. The 5 Symbols & What They Pay (Match 3)
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2.5, px: { xs: 2, sm: 3 } }}>
            {/* Theme Toggle Tab */}
            <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-start" }}>
              <Tabs
                value={selectedThemeTab}
                onChange={(e, val) => setSelectedThemeTab(val)}
                sx={{
                  bgcolor: "#f1f5f9",
                  p: "3px",
                  borderRadius: "10px",
                  "& .MuiTabs-indicator": { display: "none" },
                }}
              >
                <Tab
                  value="fiat"
                  label="Classic Vegas Edition"
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.78rem",
                    textTransform: "none",
                    minHeight: "32px",
                    borderRadius: "8px",
                    color: selectedThemeTab === "fiat" ? "#0f172a !important" : "#64748b",
                    bgcolor: selectedThemeTab === "fiat" ? "#ffffff !important" : "transparent",
                    boxShadow: selectedThemeTab === "fiat" ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
                  }}
                />
                <Tab
                  value="crypto"
                  label="Crypto 3D Edition"
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.78rem",
                    textTransform: "none",
                    minHeight: "32px",
                    borderRadius: "8px",
                    color: selectedThemeTab === "crypto" ? "#0f172a !important" : "#64748b",
                    bgcolor: selectedThemeTab === "crypto" ? "#ffffff !important" : "transparent",
                    boxShadow: selectedThemeTab === "crypto" ? "0 1px 4px rgba(0,0,0,0.06)" : "none",
                  }}
                />
              </Tabs>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "12px" }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, color: "#475569" }}>Contract #</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: "#475569" }}>Symbol</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 800, color: "#475569" }}>Base Multiplier</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: "#475569" }}>Bonus / Jackpot Awarded</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {symbols.map((sym) => (
                    <TableRow key={sym.id} sx={{ "&:hover": { bgcolor: "#faf5ff" } }}>
                      <TableCell sx={{ fontWeight: 900, color: "#64748b", fontFamily: "monospace" }}>
                        Index {sym.contractNumber}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <ThreeSlotSymbolIcon symbol={sym} size="xs" />
                          <Box>
                            <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.85rem" }}>
                              {sym.label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#64748b" }}>
                              {sym.code}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 1.25,
                            py: "3px",
                            borderRadius: "6px",
                            bgcolor: `${sym.tierColor}18`,
                            border: `1px solid ${sym.tierColor}55`,
                            color: sym.tierColor,
                            fontWeight: 900,
                            fontSize: "0.85rem",
                          }}
                        >
                          {sym.multiplier}x
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 800, color: sym.tierColor, fontSize: "0.82rem" }}>
                          {sym.winningTier || sym.winOutcome}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          {sym.description}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* SECTION 3: THE 5 PAYLINES & THEIR MULTIPLIERS */}
        <Accordion
          expanded={expandedPanel === "panel-paylines"}
          onChange={handleAccordionChange("panel-paylines")}
          sx={{
            borderRadius: "14px !important",
            border: `1.5px solid ${expandedPanel === "panel-paylines" ? "#8b5cf6" : colors.slate[200]}`,
            boxShadow: expandedPanel === "panel-paylines" ? "0 4px 16px rgba(139, 92, 246, 0.08)" : "none",
            "&:before": { display: "none" },
            overflow: "hidden",
            bgcolor: "#ffffff",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#8b5cf6" }} />}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <AltRouteIcon sx={{ color: "#8b5cf6" }} />
              <Typography sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "0.95rem" }}>
                3. The 5 Paylines & Dedicated Multipliers
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2.5, px: { xs: 2, sm: 3 } }}>
            <Typography variant="body2" sx={{ color: colors.slate[600], mb: 1.5 }}>
              Each of the 5 paylines follows a <strong>strictly fixed geometric pattern</strong> across the 3×3 grid, paired with its own dedicated multiplier:
            </Typography>

            <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "12px", mb: 2 }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, color: "#475569" }}>Payline</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: "#475569" }}>Pattern</TableCell>
                    <TableCell sx={{ fontWeight: 800, color: "#475569" }}>Where It Is</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: "#475569" }}>Multiplier</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activePaylines.map((pl) => (
                    <TableRow key={pl.id} sx={{ "&:hover": { bgcolor: "#faf5ff" } }}>
                      <TableCell sx={{ fontWeight: 800, color: "#0f172a" }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box
                            sx={{
                              width: 22,
                              height: 22,
                              borderRadius: "4px",
                              bgcolor: pl.badgeColor,
                              color: "#ffffff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "0.7rem",
                              fontWeight: 900,
                            }}
                          >
                            {pl.lineNum}
                          </Box>
                          <span>{pl.name}</span>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ color: "#334155", fontWeight: 700 }}>{pl.pattern}</TableCell>
                      <TableCell sx={{ color: "#64748b", fontSize: "0.8rem" }}>{pl.where}</TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "inline-block",
                            px: 1,
                            py: "2px",
                            borderRadius: "6px",
                            bgcolor: `${pl.color}18`,
                            border: `1px solid ${pl.color}55`,
                            color: pl.badgeColor,
                            fontWeight: 900,
                            fontSize: "0.85rem",
                          }}
                        >
                          {pl.multiplier}x
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ p: 1.25, borderRadius: "8px", bgcolor: "#f8fafc", border: "1px dashed #cbd5e1" }}>
              <Typography variant="caption" sx={{ color: "#475569" }}>
                *Note: These paylines are for 3-SlotBET. The 5-Slot format features 7 paylines with advanced patterns (V-Shape, Inverted V, Zigzag, etc.). Payline geometry remains invariant, while multipliers can scale dynamically from contract parameters.
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* SECTION 4: WINNING RULES & PAYOUT FORMULA */}
        <Accordion
          expanded={expandedPanel === "panel-formula"}
          onChange={handleAccordionChange("panel-formula")}
          sx={{
            borderRadius: "14px !important",
            border: `1.5px solid ${expandedPanel === "panel-formula" ? "#8b5cf6" : colors.slate[200]}`,
            boxShadow: expandedPanel === "panel-formula" ? "0 4px 16px rgba(139, 92, 246, 0.08)" : "none",
            "&:before": { display: "none" },
            overflow: "hidden",
            bgcolor: "#ffffff",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#8b5cf6" }} />}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CalculateIcon sx={{ color: "#8b5cf6" }} />
              <Typography sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "0.95rem" }}>
                4. Winning Rules & Exact Payout Formula
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2.5, px: { xs: 2, sm: 3 } }}>
            <Typography variant="body2" sx={{ color: colors.slate[600], mb: 1.5, lineHeight: 1.6 }}>
              <strong>Win Condition:</strong> To win on a line, all 3 spots along that active line must land the exact same symbol (<strong>3-of-a-kind</strong>). Any of the 5 symbols can trigger a win on any line.
            </Typography>

            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                bgcolor: "#0f172a",
                color: "#ffffff",
                fontFamily: "monospace",
                fontWeight: 800,
                fontSize: { xs: "0.85rem", sm: "0.95rem" },
                textAlign: "center",
                mb: 2,
                boxShadow: "inset 0 2px 4px rgba(0, 0, 0, 0.2)",
              }}
            >
              Line Payout = (Ticket Price ÷ Active Lines) × Base Symbol Multiplier × Line Multiplier
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a", mb: 1 }}>
              Worked Example (10 {currency} Ticket on 5 Active Lines = 2 {currency} per line):
            </Typography>

            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                    • 3x Bells on Line 1 (1x Multiplier):
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: "#ca8a04" }}>
                    2 {currency} × 10 (Bell) × 1x = 20 {currency}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                    • 3x Bells on Line 2 (2x Multiplier):
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: "#2563eb" }}>
                    2 {currency} × 10 (Bell) × 2x = 40 {currency}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                    • 3x Lucky 7s on Line 4 (5x Multiplier):
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: "#dc2626" }}>
                    2 {currency} × 25 (7s) × 5x = 250 {currency}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  <Typography variant="caption" sx={{ color: "#64748b", display: "block" }}>
                    • 3x Crowns on Line 5 (10x Multiplier):
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: "#d97706" }}>
                    2 {currency} × 50 (Crown) × 10x = 1,000 {currency}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#fefce8", border: "1.5px solid #fde047" }}>
              <Typography variant="body2" sx={{ color: "#854d0e", fontWeight: 700 }}>
                👑 <strong>Jackpot Rule:</strong> If you hit 3 matching Bells (Mini), Diamonds (Major), or Crowns (Mega), you receive the <strong>Multiplied Line Win PLUS the full cash amount in that Jackpot Pool!</strong>
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* SECTION 5: CREATOR SEED MONEY FORMULA */}
        <Accordion
          expanded={expandedPanel === "panel-seed"}
          onChange={handleAccordionChange("panel-seed")}
          sx={{
            borderRadius: "14px !important",
            border: `1.5px solid ${expandedPanel === "panel-seed" ? "#8b5cf6" : colors.slate[200]}`,
            boxShadow: expandedPanel === "panel-seed" ? "0 4px 16px rgba(139, 92, 246, 0.08)" : "none",
            "&:before": { display: "none" },
            overflow: "hidden",
            bgcolor: "#ffffff",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#8b5cf6" }} />}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <MonetizationOnIcon sx={{ color: "#8b5cf6" }} />
              <Typography sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "0.95rem" }}>
                5. How Creator Seed Money is Calculated
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2.5, px: { xs: 2, sm: 3 } }}>
            <Typography variant="body2" sx={{ color: colors.slate[600], mb: 1.5 }}>
              When creating the lottery, the creator deposits an upfront seed amount to bankroll the game. This covers two dedicated reserves:
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "#fefce8", border: "1px solid #fde047", height: "100%" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#854d0e", mb: 1 }}>
                    Part 1: Jackpot Starting Pools (30,500 {currency})
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#713f12", lineHeight: 1.6, fontSize: "0.85rem" }}>
                    • Mini Jackpot Starting Pool: <strong>500 {currency}</strong><br />
                    • Major Jackpot Starting Pool: <strong>5,000 {currency}</strong><br />
                    • Mega Jackpot Starting Pool: <strong>25,000 {currency}</strong><br />
                    <strong>Subtotal = 30,500 {currency}</strong>
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "#faf5ff", border: "1px solid #d8b4fe", height: "100%" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#6b21a8", mb: 1 }}>
                    Part 2: Multiplier Payout Buffer (5,000 {currency})
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#581c87", lineHeight: 1.6, fontSize: "0.85rem" }}>
                    Backs big multiplier wins (e.g. Line 5 paying 10x) from spin #1.<br />
                    • <strong>Formula:</strong> (Bet Per Line) × (Top Symbol: 50x) × (Top Multiplier: 10x) × Safety Factor<br />
                    <strong>Subtotal = 5,000 {currency}</strong>
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "#f8fafc", border: "1.5px solid #cbd5e1" }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems="center" spacing={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, color: "#0f172a" }}>
                  TOTAL CREATOR SEED DEPOSIT:
                </Typography>
                <Chip
                  label={`35,500 ${currency} (Vault Backed)`}
                  sx={{ bgcolor: "#8b5cf6", color: "#ffffff", fontWeight: 900, fontSize: "0.85rem" }}
                />
              </Stack>
              <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 1 }}>
                *Note: This money is <strong>NOT spent or lost</strong>. It stays in the smart contract vault during the lottery. Unused seed money + accumulated house profits are returned to the creator when the lottery ends!
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* SECTION 6 & 7: MONEY SPLIT & WITHDRAWAL RULES */}
        <Accordion
          expanded={expandedPanel === "panel-economics"}
          onChange={handleAccordionChange("panel-economics")}
          sx={{
            borderRadius: "14px !important",
            border: `1.5px solid ${expandedPanel === "panel-economics" ? "#8b5cf6" : colors.slate[200]}`,
            boxShadow: expandedPanel === "panel-economics" ? "0 4px 16px rgba(139, 92, 246, 0.08)" : "none",
            "&:before": { display: "none" },
            overflow: "hidden",
            bgcolor: "#ffffff",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#8b5cf6" }} />}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <PieChartIcon sx={{ color: "#8b5cf6" }} />
              <Typography sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "0.95rem" }}>
                6 & 7. Split Economics & Withdrawal Rules
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2.5, px: { xs: 2, sm: 3 } }}>
            <Typography variant="body2" sx={{ color: colors.slate[600], mb: 2 }}>
              Every spin wager (e.g. 100 {currency}) is transparently split by the smart contract on-chain:
            </Typography>

            <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
              <Grid item xs={6} sm={3}>
                <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#fefce8", border: "1px solid #fde047", textAlign: "center" }}>
                  <Typography variant="h6" sx={{ fontWeight: 900, color: "#854d0e" }}>3.5%</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: "#713f12" }}>Jackpots Pool</Typography>
                  <Typography variant="caption" sx={{ display: "block", color: "#a16207", fontSize: "0.68rem" }}>Mini, Major & Mega</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#faf5ff", border: "1px solid #d8b4fe", textAlign: "center" }}>
                  <Typography variant="h6" sx={{ fontWeight: 900, color: "#6b21a8" }}>10.0%</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: "#581c87" }}>Creator Cut</Typography>
                  <Typography variant="caption" sx={{ display: "block", color: "#7e22ce", fontSize: "0.68rem" }}>Anytime Withdrawal</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", textAlign: "center" }}>
                  <Typography variant="h6" sx={{ fontWeight: 900, color: "#166534" }}>10.0%</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: "#14532d" }}>Admin Fee</Typography>
                  <Typography variant="caption" sx={{ display: "block", color: "#15803d", fontSize: "0.68rem" }}>Platform Protocol</Typography>
                </Box>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#eff6ff", border: "1px solid #bfdbfe", textAlign: "center" }}>
                  <Typography variant="h6" sx={{ fontWeight: 900, color: "#1e40af" }}>76.5%</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: "#172554" }}>Payout Reserve</Typography>
                  <Typography variant="caption" sx={{ display: "block", color: "#2563eb", fontSize: "0.68rem" }}>Locked for Players</Typography>
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a", mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}>
              <LockClockIcon sx={{ fontSize: 18, color: "#8b5cf6" }} /> Strict Withdrawal Rules
            </Typography>
            <Typography variant="body2" sx={{ color: colors.slate[600], lineHeight: 1.6 }}>
              • <strong>Creator Commission (10%):</strong> Can be withdrawn at <em>ANY TIME</em> by the lottery owner.<br />
              • <strong>Admin Fee (10%):</strong> Can be withdrawn at <em>ANY TIME</em> by platform administration.<br />
              • <strong>Remaining 76.5% Reserve:</strong> Strictly <strong>LOCKED</strong> in the smart contract while the game is active to guarantee player payouts. It can <strong>ONLY</strong> be distributed to the creator after the lottery End Date has passed.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* SECTION 8: STEP-BY-STEP SPIN FLOW */}
        <Accordion
          expanded={expandedPanel === "panel-flow"}
          onChange={handleAccordionChange("panel-flow")}
          sx={{
            borderRadius: "14px !important",
            border: `1.5px solid ${expandedPanel === "panel-flow" ? "#8b5cf6" : colors.slate[200]}`,
            boxShadow: expandedPanel === "panel-flow" ? "0 4px 16px rgba(139, 92, 246, 0.08)" : "none",
            "&:before": { display: "none" },
            overflow: "hidden",
            bgcolor: "#ffffff",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#8b5cf6" }} />}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <TimelineIcon sx={{ color: "#8b5cf6" }} />
              <Typography sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "0.95rem" }}>
                8. Step-by-Step Spin Execution Flow
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2.5, px: { xs: 2, sm: 3 } }}>
            <Stack spacing={1.25}>
              {[
                { step: "1", title: "Click SPIN", desc: "Player clicks SPIN and signs the transaction (paying ticket price)." },
                { step: "2", title: "Instant Fee Split", desc: "Money is split instantly: 3.5% Jackpots, 10% Creator, 10% Admin, 76.5% Payout Reserve." },
                { step: "3", title: "Fair RNG Call", desc: "Fair RNG determines the 3 reel stop positions on-chain." },
                { step: "4", title: "Reels Spin & Stop", desc: "The 3 reels spin and reveal all 9 symbol positions across the 3 rows." },
                { step: "5", title: "Check Active Paylines", desc: "The contract and client evaluate all lines matching the player's active line selection." },
                { step: "6", title: "Multiplied Payouts", desc: "Matching 3-of-a-kind lines are multiplied by that line's multiplier (1x to 10x)." },
                { step: "7", title: "Jackpot & Bonus Awards", desc: "If Bells, Diamonds, or Crowns match, the full Jackpot cash pool is awarded on top." },
                { step: "8", title: "Wallet Deposit", desc: "Total winnings are credited directly into the player's wallet balance." },
              ].map((item) => (
                <Box
                  key={item.step}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.25,
                    borderRadius: "10px",
                    bgcolor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      bgcolor: "#8b5cf6",
                      color: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.78rem",
                      fontWeight: 900,
                      flexShrink: 0,
                    }}
                  >
                    {item.step}
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.85rem" }}>
                      {item.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* SECTION 9: 5-SLOT REEL FORMAT PREVIEW */}
        <Accordion
          expanded={expandedPanel === "panel-5slot"}
          onChange={handleAccordionChange("panel-5slot")}
          sx={{
            borderRadius: "14px !important",
            border: `1.5px solid ${expandedPanel === "panel-5slot" ? "#8b5cf6" : colors.slate[200]}`,
            boxShadow: expandedPanel === "panel-5slot" ? "0 4px 16px rgba(139, 92, 246, 0.08)" : "none",
            "&:before": { display: "none" },
            overflow: "hidden",
            bgcolor: "#ffffff",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#8b5cf6" }} />}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CasinoIcon sx={{ color: "#8b5cf6" }} />
              <Typography sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "0.95rem" }}>
                9. 5-Slot Reel Format (Preview & Comparison)
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2.5, px: { xs: 2, sm: 3 } }}>
            <Typography variant="body2" sx={{ color: colors.slate[600], mb: 1.5 }}>
              In addition to the 3-Reel format, the platform offers a 5-Reel format with expanded paylines and symbol sets:
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "#f8fafc", border: "1px solid #e2e8f0", height: "100%" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#8b5cf6", mb: 0.5 }}>
                    Screen & 7 Advanced Paylines
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.slate[600], fontSize: "0.85rem", lineHeight: 1.6 }}>
                    • Screen: <strong>5 reels × 3 rows (15 positions)</strong><br />
                    • Line 1 (Top: 1x), Line 2 (Mid: 1x), Line 3 (Btm: 2x)<br />
                    • Line 4 (V-Shape: 5x), Line 5 (Inverted V: 7x)<br />
                    • Line 6 (Zigzag: 10x), Line 7 (Reverse Zigzag: 15x)
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 2, borderRadius: "12px", bgcolor: "#f8fafc", border: "1px solid #e2e8f0", height: "100%" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#8b5cf6", mb: 0.5 }}>
                    Extra Symbols & Multi-Match Evaluation
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.slate[600], fontSize: "0.85rem", lineHeight: 1.6 }}>
                    • 🍀 <strong>Lucky Clover (20x):</strong> High-value payline symbol.<br />
                    • 🪙 <strong>Golden Coin (100x):</strong> Ultimate payout symbol.<br />
                    • Evaluates 3-match, 4-match, or 5-match wins left-to-right along each payline.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* SECTION 10: PLAYER PAYLINE SELECTION (FLEXIBLE LINE BETTING) */}
        <Accordion
          expanded={expandedPanel === "panel-controls"}
          onChange={handleAccordionChange("panel-controls")}
          sx={{
            borderRadius: "14px !important",
            border: `1.5px solid ${expandedPanel === "panel-controls" ? "#8b5cf6" : colors.slate[200]}`,
            boxShadow: expandedPanel === "panel-controls" ? "0 4px 16px rgba(139, 92, 246, 0.08)" : "none",
            "&:before": { display: "none" },
            overflow: "hidden",
            bgcolor: "#ffffff",
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#8b5cf6" }} />}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <TuneIcon sx={{ color: "#8b5cf6" }} />
              <Typography sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "0.95rem" }}>
                10. Player Payline Selection (Flexible Line Betting)
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2.5, px: { xs: 2, sm: 3 } }}>
            <Typography variant="body2" sx={{ color: colors.slate[600], mb: 1.5, lineHeight: 1.6 }}>
              Players have complete control over their wager by choosing 1 or more paylines while playing:
            </Typography>

            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                    🎯 Line Toggle Buttons (1 to {activePaylines.length})
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.25 }}>
                    Toggle individual lines on or off (e.g., bet only on Line 5 to chase the 10x multiplier).
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                    ⚡ Single Line (L1) Button
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.25 }}>
                    Instantly activates only Line 1 (middle horizontal row) so 100% of your bet is concentrated on Line 1.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                    🌟 All Lines / Max Bet Button
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.25 }}>
                    Instantly activates all available lines for maximum win coverage across the 9 positions.
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box sx={{ p: 1.5, borderRadius: "10px", bgcolor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a" }}>
                    🚫 Non-Selected Lines Rule
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.25 }}>
                    If a winning pattern lands on a line that you did NOT activate, that line does NOT pay out. Only activated lines pay.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Box>
  );
};

export default ThreeSlotRulesSection;
